import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import './admin.css';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;

function getApiError(err) {
  const data = err.response?.data;
  if (!data) return err.message || 'Something went wrong';
  if (typeof data === 'string') return data;
  return (
    data.error ||
    data.message ||
    data.msg ||
    data.detail ||
    'Something went wrong'
  );
}

export default function AdminForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const sendOtp = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Please enter your admin email address');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await api.post('admin/forgot-password/', { email: trimmed });
      setOtpSent(true);
      setOtpVerified(false);
      setOtp('');
      toast.success('If this email is registered, an OTP has been sent.', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedOtp = otp.trim();
    if (!trimmedOtp) {
      setError('Please enter the OTP from your email');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await api.post('admin/forgot-password-verify-otp/', {
        email: trimmedEmail,
        otp: trimmedOtp,
      });
      setOtpVerified(true);
      toast.success('OTP verified. Set your new password below.', {
        position: 'bottom-right',
        autoClose: 3000,
      });
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();

    if (!otpSent) {
      setError('Please send OTP to your email first');
      return;
    }
    if (!otpVerified) {
      setError('Please verify OTP before resetting password');
      return;
    }
    if (!PASSWORD_REGEX.test(newPassword)) {
      setError(
        'Password must have 8+ characters, uppercase, lowercase, number and special character (@$!%*#?&)'
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirm password do not match');
      return;
    }

    setError('');
    setLoading(true);
    try {
      await api.post('admin/reset-password/', {
        email: trimmedEmail,
        new_password: newPassword,
      });
      toast.success('Password reset successful! Please login.', {
        position: 'bottom-right',
        autoClose: 2500,
      });
      setTimeout(() => navigate('/admin/login'), 1200);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login position-relative">
      <Link
        to="/admin/login"
        className="btn btn-link btn-sm text-muted text-decoration-none position-absolute top-0 start-0 m-3"
      >
        ← Back to Login
      </Link>

      <div className="admin-login-card">
        <div className="admin-login-logo">
          <div className="admin-login-logo-mark">
            <i className="bi bi-key-fill" />
          </div>
          <div className="admin-login-logo-name">FITNOVA</div>
          <div className="admin-login-logo-tag">ADMIN PASSWORD RESET</div>
        </div>

        <p className="admin-login-lead">
          Enter the email linked to your staff account to receive an OTP
        </p>

        {error && (
          <div className="admin-login-alert" role="alert">
            <i className="bi bi-exclamation-circle-fill" />
            <span>{error}</span>
            <button type="button" onClick={() => setError('')} aria-label="Dismiss">
              <i className="bi bi-x-lg" />
            </button>
          </div>
        )}

        <form onSubmit={resetPassword} className="admin-login-form">
          <label className="admin-login-label" htmlFor="admin-reset-email">
            Email Address
          </label>
          <div className="admin-login-field">
            <i className="bi bi-envelope" />
            <input
              id="admin-reset-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@fitnova.com"
              autoComplete="email"
              required
              disabled={loading || otpVerified}
            />
          </div>

          {!otpVerified && (
            <button
              type="button"
              className="admin-login-submit"
              onClick={sendOtp}
              disabled={loading}
            >
              <i className="bi bi-send" />
              {loading && !otpSent ? 'Sending…' : otpSent ? 'Resend OTP' : 'Send OTP to Email'}
            </button>
          )}

          {otpSent && !otpVerified && (
            <>
              <label className="admin-login-label" htmlFor="admin-reset-otp">
                Enter OTP
              </label>
              <div className="admin-login-field">
                <i className="bi bi-shield-check" />
                <input
                  id="admin-reset-otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit OTP"
                  maxLength={6}
                  inputMode="numeric"
                  required
                  disabled={loading}
                />
              </div>

              <button
                type="button"
                className="admin-login-submit"
                onClick={verifyOtp}
                disabled={loading || otp.length !== 6}
              >
                <i className="bi bi-patch-check" />
                {loading ? 'Verifying…' : 'Verify OTP'}
              </button>
            </>
          )}

          {otpVerified && (
            <>
              <label className="admin-login-label" htmlFor="admin-reset-password">
                New Password
              </label>
              <div className="admin-login-field">
                <i className="bi bi-lock" />
                <input
                  id="admin-reset-password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password"
                  autoComplete="new-password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="admin-login-eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                </button>
              </div>

              <label className="admin-login-label" htmlFor="admin-reset-confirm">
                Confirm Password
              </label>
              <div className="admin-login-field">
                <i className="bi bi-lock-fill" />
                <input
                  id="admin-reset-confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  required
                  disabled={loading}
                />
              </div>
              <p className="admin-login-hint">
                Min 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special (@$!%*#?&)
              </p>

              <button type="submit" className="admin-login-submit" disabled={loading}>
                <i className="bi bi-arrow-repeat" />
                {loading ? 'Resetting…' : 'Reset Password'}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
